import type { SelectItem } from '@vrplatform/components';

type TeamSwitchPage = {
  list: SelectItem[];
  aggregate: number;
};

const RECENTLY_ACCESSED_GROUP = 'Recently accessed';
const ALL_TEAMS_GROUP = 'All teams';
export const MIN_TEAM_SWITCH_GROUPED_TEAM_COUNT = 4;

const withGroup = <T extends SelectItem>(item: T, group: string): T => ({
  ...item,
  group,
});

export const shouldGroupTeamSwitchItems = (totalTeamCount: number) =>
  totalTeamCount >= MIN_TEAM_SWITCH_GROUPED_TEAM_COUNT;

export const buildTeamSwitchPinnedItems = ({
  recentTeamIds,
  recentTeams,
  shouldGroupTeams,
}: {
  recentTeamIds: readonly string[];
  recentTeams: readonly SelectItem[];
  shouldGroupTeams: boolean;
}) => {
  if (!shouldGroupTeams) {
    return {
      pinnedItems: [],
      pinnedItemIds: new Set<string>(),
    };
  }

  const recentTeamsById = new Map(
    recentTeams.map((item) => [item.value, item] as const)
  );

  const orderedRecentTeams = recentTeamIds
    .map((teamId) => recentTeamsById.get(teamId))
    .filter((item): item is SelectItem => !!item)
    .map((item) => withGroup(item, RECENTLY_ACCESSED_GROUP));

  return {
    pinnedItems: orderedRecentTeams,
    pinnedItemIds: new Set(orderedRecentTeams.map((item) => item.value)),
  };
};

export const buildTeamSwitchVisibleData = ({
  pages,
  recentTeamIds,
  recentTeams,
  shouldGroupTeams,
}: {
  pages: readonly TeamSwitchPage[];
  recentTeamIds: readonly string[];
  recentTeams: readonly SelectItem[];
  shouldGroupTeams: boolean;
}) => {
  const recentTeamIdSet = new Set(recentTeamIds);
  const filteredPages = shouldGroupTeams
    ? pages.map((page) => ({
        ...page,
        list: page.list.filter((item) => !recentTeamIdSet.has(item.value)),
      }))
    : pages;

  const { pinnedItems, pinnedItemIds } = buildTeamSwitchPinnedItems({
    recentTeamIds,
    recentTeams,
    shouldGroupTeams,
  });

  return {
    pinnedItems,
    pages: filterTeamSwitchPages({
      pages: filteredPages,
      pinnedItemIds,
      showTeamGroups: shouldGroupTeams,
    }),
  };
};

export const resolveTeamSwitchValue = ({
  dashboard,
  currentTeam,
  hydratedTeamId,
  hydratedTeamName,
  selectedTeamId,
}: {
  dashboard: string | null | undefined;
  currentTeam?: SelectItem | null;
  hydratedTeamId?: string | null;
  hydratedTeamName?: string | null;
  selectedTeamId?: string | null;
}): SelectItem | null => {
  if (dashboard === 'overview') return null;

  if (selectedTeamId && currentTeam?.value === selectedTeamId) {
    return currentTeam;
  }

  if (hydratedTeamId) {
    return {
      label: hydratedTeamName || '',
      value: hydratedTeamId,
    };
  }

  return currentTeam ?? null;
};

export const filterTeamSwitchPages = ({
  pages,
  pinnedItemIds,
  showTeamGroups,
}: {
  pages: readonly TeamSwitchPage[];
  pinnedItemIds: ReadonlySet<string>;
  showTeamGroups: boolean;
}) => {
  const mapTeamItem = (item: SelectItem) =>
    showTeamGroups ? withGroup(item, ALL_TEAMS_GROUP) : item;

  return pages.map((page) => ({
    ...page,
    list: page.list
      .filter((item) => !pinnedItemIds.has(item.value))
      .map(mapTeamItem),
  }));
};
